/**
 * Button — MCK-20260521-003 Tier 1 primitive.
 *
 * Variants Primary / Secondary / Tertiary / Danger, sizes sm / md / lg, and the five states
 * the spec names: default, hover, pressed, focus-visible, disabled, loading.
 *
 * `loading` disables the button as well as marking it: a submit that is already in flight
 * must not be submittable again, and leaving that to the caller is how double-invitations
 * happen. It no longer *dims* it, though — see `Button.module.css`, the `:disabled` block.
 */

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import styles from './Button.module.css';
import { RasterLoader } from './RasterLoader';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Stretches to the container. The entrance forms are full-width single-column. */
  readonly fullWidth?: boolean;
  /** Shows the raster mark and blocks interaction. Implies `disabled`. */
  readonly loading?: boolean;
  /** Rendered before the label. Lucide, 1.5px stroke — one family, so the UI has one
   * drawing hand. The single exception is the identity-provider marks in
   * `features/entrance/ProviderMarks`: a brand mark is a filled shape and outlining it to
   * match the stroke family would make it unrecognisable without making it compliant. */
  readonly icon?: ReactNode;
  readonly children: ReactNode;
  /** React 19's ref-as-prop, reaching the `<button>` through `rest` — for a screen that moves
   * focus to its one next step on arrival (`ProjectsPage`'s „Vytvor prvý projekt“). */
  readonly ref?: Ref<HTMLButtonElement>;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  fullWidth = false,
  loading = false,
  icon,
  children,
  type = 'button',
  disabled = false,
  ...rest
}: ButtonProps) {
  const classes = [styles.button, styles[variant], styles[size], fullWidth ? styles.fullWidth : '']
    .filter(Boolean)
    .join(' ');

  return (
    <button
      {...rest}
      type={type}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
    >
      {/* The raster is 13px whatever the size, deliberately: it stands in for the icon slot and
          a mark that grew with the button would push a `sm` one off its 26px. Ink on the
          primary's `neutral-900`, paper on every other variant's light ground. */}
      {loading ? <RasterLoader tone={variant === 'primary' ? 'ink' : 'paper'} /> : icon}
      <span className={styles.label}>{children}</span>
    </button>
  );
}
